import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Radio, Crown, Mic2, Upload, X, Check, Clock, Sparkles } from "lucide-react";

const SPOTIFY_PLAYLIST_ID = "0Z4ZR8MGvVOIvWnVHZFFqB";
const YUMEPAI_IMG = "https://customer-assets.emergentagent.com/job_creators-stage-1/artifacts/leq9ab9r_IMG_0767.png";

export default function RadioStation() {
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [mySubs, setMySubs] = useState([]);
  const [loadingPay, setLoadingPay] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    artist_name: "", track_title: "", track_link: "", genre: "", note: "",
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const loadQuota = useCallback(() => {
    if (!user) { setQuota(null); setMySubs([]); return; }
    api.get("/radio/me/quota").then((r) => setQuota(r.data)).catch(() => setQuota(null));
    api.get("/radio/submissions/mine").then((r) => setMySubs(r.data)).catch(() => setMySubs([]));
  }, [user]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  const buySubmission = async () => {
    setLoadingPay(true);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: "radio_submission",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoadingPay(false);
    }
  };

  const submitTrack = async (e) => {
    e.preventDefault();
    if (!form.artist_name.trim() || !form.track_title.trim() || !form.track_link.trim()) {
      toast.error("Artist name, track title, and link are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/radio/submissions", form);
      toast.success("Track submitted! Our team will review and email you feedback within 5 business days.");
      setForm({ artist_name: "", track_title: "", track_link: "", genre: "", note: "" });
      setShowSubmit(false);
      loadQuota();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter/subscribe", { email: newsletterEmail.trim(), source: "radio" });
      toast.success("Check your inbox to confirm your PFC newsletter subscription, then come back.");
      setNewsletterEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubscribing(false);
    }
  };

  const isSubscribed = quota?.is_newsletter_subscribed;
  const canSubmit = user && isSubscribed && quota && quota.remaining > 0;

  return (
    <div data-testid="radio-page">
      {/* HERO */}
      <section className="relative py-20 border-b border-white/5 splatter-blue grain overflow-hidden">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-10 relative">
          <div className="flex items-center gap-3 mb-4">
            <Radio className="text-pfc-gold animate-pulse" size={20} />
            <div className="label text-pfc-gold">PFC · Live Radio</div>
            <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-1 bg-pfc-red/20 border border-pfc-red/40 rounded-sm">
              <span className="w-1.5 h-1.5 bg-pfc-red rounded-full animate-pulse" />
              <span className="font-sub text-[10px] uppercase tracking-[0.25em] text-pfc-red">ON AIR</span>
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white uppercase leading-[0.9]">
            The Music <span className="gold-text">Hitlist</span><br />
            <span className="silver-text">Radio.</span>
          </h1>
          <p className="text-white/70 mt-6 font-body text-lg max-w-2xl">
            The official PFC playlist. Streaming the artists driving the campaign — heavy rotation,
            curated by our editorial team. Want your track on the air? Submit for $19.99 per record.
          </p>
        </div>
      </section>

      {/* YUMEPAI HEADLINE BANNER */}
      <section className="border-b border-white/10 bg-gradient-to-r from-pfc-gold/10 via-transparent to-pfc-red/10" data-testid="radio-yumepai-headline">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-10 py-6 flex items-center gap-5 flex-wrap">
          <div className="relative shrink-0">
            <img
              src={YUMEPAI_IMG}
              alt="Yumepai · Now Spinning"
              className="w-20 h-20 object-cover border-2 border-pfc-gold"
              style={{ borderRadius: 4 }}
            />
            <span className="absolute -bottom-2 -right-2 px-2 py-0.5 bg-pfc-gold text-ink-900 font-sub text-[9px] uppercase tracking-[0.2em]">
              Headline
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="label text-pfc-gold mb-1">Now Spinning · Station Headline</div>
            <h3 className="font-display text-2xl sm:text-3xl text-white uppercase leading-tight truncate">
              Yumepai — <span className="gold-text">Turned Chic</span>
            </h3>
            <p className="text-white/60 font-body text-sm mt-1">
              Atlanta's hyperpop-rave queen leads the playlist. Promo album streaming now on Spotify & Apple Music.
            </p>
          </div>
          <Link
            to="/directory?search=yumepai"
            className="font-sub uppercase tracking-[0.25em] text-xs px-5 py-3 border border-pfc-gold/40 text-pfc-gold hover:bg-pfc-gold hover:text-ink-900 transition"
            data-testid="radio-yumepai-cta"
          >
            View Profile →
          </Link>
        </div>
      </section>

      {/* SPOTIFY EMBED */}
      <section className="max-w-[1300px] mx-auto px-5 lg:px-10 py-14" data-testid="radio-player-section">
        <div className="label text-pfc-gold mb-3">The Stream</div>
        <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-6">
          Press <span className="gold-text">Play</span>. Stay <span className="silver-text">Loud</span>.
        </h2>
        <iframe
          title="The Music Hitlist Radio · PFC Playlist"
          src={`https://open.spotify.com/embed/playlist/${SPOTIFY_PLAYLIST_ID}?utm_source=generator&theme=0`}
          width="100%"
          height="480"
          frameBorder="0"
          allowFullScreen=""
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ borderRadius: 12 }}
          data-testid="radio-spotify-embed"
        />
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-white/50 font-body text-xs">
            Embed not loading? Spotify sometimes blocks the player in certain browsers — open the playlist directly:
          </p>
          <a
            href={`https://open.spotify.com/playlist/${SPOTIFY_PLAYLIST_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-sub uppercase tracking-[0.2em] text-xs px-5 py-2.5 border border-pfc-gold/40 text-pfc-gold hover:bg-pfc-gold hover:text-ink-900 transition inline-flex items-center gap-2"
            data-testid="radio-open-spotify"
          >
            <Radio size={14} /> Open in Spotify
          </a>
        </div>
      </section>

      {/* SUBMIT CTA */}
      <section className="border-t border-white/5 bg-ink-900 py-20" id="submit" data-testid="radio-submit-section">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="label text-pfc-gold mb-3">Track Submissions</div>
              <h2 className="font-display text-3xl sm:text-5xl text-white uppercase leading-[0.95]">
                Get on the <span className="gold-text">Hitlist.</span>
              </h2>
              <p className="text-white/65 mt-5 font-body text-base">
                Submit your music for consideration on The Music Hitlist Radio. Each submission is reviewed
                by the PFC editorial team. <strong className="text-white">All submitters receive feedback</strong>
                {" "}on their track, even if it's not added to the playlist.
              </p>

              <ul className="space-y-3 mt-7" data-testid="radio-submit-features">
                {[
                  "$19.99 per track submission · Stripe secure checkout",
                  "Must be a confirmed PFC newsletter subscriber",
                  "Personalised editorial feedback on your record",
                  "Selected tracks added to The Music Hitlist Radio · driving streams",
                  "Submission does not guarantee placement — music must sound great",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/80 font-body text-sm">
                    <Check className="text-pfc-gold shrink-0 mt-0.5" size={16} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT: GATING PANEL */}
            <div className="pfc-card p-8 lg:p-10" data-testid="radio-gating-panel">
              {!user && (
                <div data-testid="radio-gate-login">
                  <Mic2 className="text-pfc-gold mb-4" size={28} />
                  <h3 className="font-display text-2xl text-white uppercase mb-2">Sign In to Submit</h3>
                  <p className="text-white/60 font-body text-sm mb-6">
                    Create a free PFC account or sign in to submit a track for the radio.
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <Link to="/login" className="font-sub uppercase tracking-[0.25em] text-sm px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition" data-testid="radio-login-cta">
                      Sign In
                    </Link>
                    <Link to="/register" className="font-sub uppercase tracking-[0.25em] text-sm px-6 py-3 border border-white/20 text-white hover:border-pfc-gold transition" data-testid="radio-register-cta">
                      Register
                    </Link>
                  </div>
                </div>
              )}

              {user && quota === null && (
                <div className="text-white/50 font-sub uppercase tracking-[0.2em] text-sm">Loading your status…</div>
              )}

              {user && quota !== null && !isSubscribed && (
                <div data-testid="radio-gate-newsletter">
                  <Sparkles className="text-pfc-gold mb-4" size={28} />
                  <h3 className="font-display text-2xl text-white uppercase mb-2">
                    Subscribe to <span className="gold-text">Submit</span>
                  </h3>
                  <p className="text-white/60 font-body text-sm mb-5">
                    Radio submissions are open to confirmed PFC newsletter subscribers only.
                    Subscribe below, confirm via email, then come back to submit.
                  </p>
                  <form onSubmit={subscribeNewsletter} className="flex gap-2 flex-wrap">
                    <input
                      type="email"
                      required
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder={user.email}
                      className="flex-1 min-w-[200px] bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                      data-testid="radio-newsletter-input"
                    />
                    <button
                      type="submit"
                      disabled={subscribing}
                      className="font-sub uppercase tracking-[0.25em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
                      data-testid="radio-newsletter-submit"
                    >
                      {subscribing ? "…" : "Subscribe"}
                    </button>
                  </form>
                  <p className="text-white/40 font-body text-xs mt-3">
                    Already subscribed? <button onClick={loadQuota} className="text-pfc-gold hover:underline">Refresh status</button>
                  </p>
                </div>
              )}

              {user && quota !== null && isSubscribed && quota.remaining === 0 && (
                <div data-testid="radio-gate-payment">
                  <Crown className="text-pfc-gold mb-4" size={28} />
                  <h3 className="font-display text-2xl text-white uppercase mb-2">
                    <span className="gold-text">$19.99</span> per Track
                  </h3>
                  <p className="text-white/60 font-body text-sm mb-6">
                    Secure Stripe checkout. After payment, you'll be redirected back and the submission
                    form will unlock here.
                  </p>
                  <button
                    onClick={buySubmission}
                    disabled={loadingPay}
                    className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
                    data-testid="radio-buy-cta"
                  >
                    {loadingPay ? "Redirecting…" : "Pay $19.99 — Buy Submission"}
                  </button>
                  {quota.submissions_used > 0 && (
                    <p className="text-white/45 font-body text-xs mt-4">
                      You've submitted {quota.submissions_used} track{quota.submissions_used === 1 ? "" : "s"} previously.
                    </p>
                  )}
                </div>
              )}

              {canSubmit && (
                <div data-testid="radio-gate-submit">
                  <Upload className="text-pfc-gold mb-4" size={28} />
                  <h3 className="font-display text-2xl text-white uppercase mb-2">
                    Ready to <span className="gold-text">Submit</span>
                  </h3>
                  <p className="text-white/60 font-body text-sm mb-6">
                    You have <strong className="text-white">{quota.remaining} submission{quota.remaining === 1 ? "" : "s"}</strong> ready to go. Drop your track below.
                  </p>
                  <button
                    onClick={() => setShowSubmit(true)}
                    className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition flex items-center justify-center gap-2"
                    data-testid="radio-open-submit"
                  >
                    <Upload size={16} /> Submit Track
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* USER'S PAST SUBMISSIONS */}
          {user && mySubs.length > 0 && (
            <div className="mt-16" data-testid="radio-my-submissions">
              <div className="label text-pfc-gold mb-2">Your Submissions</div>
              <h3 className="font-display text-2xl text-white uppercase mb-6">Track <span className="gold-text">History</span></h3>
              <div className="space-y-3">
                {mySubs.map((s) => (
                  <div key={s.submission_id} className="pfc-card p-5 flex items-start gap-4 flex-wrap" data-testid={`radio-sub-${s.submission_id}`}>
                    <div className="flex-1 min-w-[240px]">
                      <h4 className="font-display text-lg text-white uppercase">{s.track_title}</h4>
                      <p className="font-sub uppercase tracking-[0.2em] text-xs text-pfc-gold mt-1">{s.artist_name}</p>
                      {s.feedback && (
                        <p className="text-white/70 font-body text-sm mt-3 pl-3 border-l-2 border-pfc-gold/40">
                          <span className="label text-pfc-gold mr-2">Editor:</span>{s.feedback}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {s.status === "pending" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/15 font-sub text-[10px] uppercase tracking-[0.25em] text-white/70">
                          <Clock size={12} /> Pending Review
                        </span>
                      )}
                      {s.status === "accepted" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pfc-gold/20 border border-pfc-gold/40 font-sub text-[10px] uppercase tracking-[0.25em] text-pfc-gold">
                          <Check size={12} /> Added to Playlist
                        </span>
                      )}
                      {s.status === "rejected" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pfc-red/10 border border-pfc-red/30 font-sub text-[10px] uppercase tracking-[0.25em] text-pfc-red">
                          Not This Time
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SUBMIT MODAL */}
      {showSubmit && (
        <div
          className="fixed inset-0 bg-ink-900/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => !submitting && setShowSubmit(false)}
          data-testid="radio-submit-modal"
        >
          <div
            className="bg-ink-800 border border-pfc-gold/30 w-full max-w-xl p-8 lg:p-10 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !submitting && setShowSubmit(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white p-2"
              data-testid="radio-submit-close"
            >
              <X size={20} />
            </button>
            <div className="label text-pfc-gold mb-2">Music Hitlist Radio · Submission</div>
            <h3 className="font-display text-3xl text-white uppercase leading-tight mb-2">
              Drop Your <span className="gold-text">Record.</span>
            </h3>
            <p className="text-white/60 font-body text-sm mb-6">
              Reviewed by the PFC editorial team. You'll get feedback by email within 5 business days.
            </p>

            <form onSubmit={submitTrack} className="space-y-4" data-testid="radio-submit-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label text-white/70 mb-2 block">Artist Name</label>
                  <input
                    type="text" required maxLength={120}
                    value={form.artist_name}
                    onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                    placeholder="Stage name / @handle"
                    className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                    data-testid="radio-form-artist"
                  />
                </div>
                <div>
                  <label className="label text-white/70 mb-2 block">Genre</label>
                  <input
                    type="text" maxLength={60}
                    value={form.genre}
                    onChange={(e) => setForm({ ...form, genre: e.target.value })}
                    placeholder="Hyperpop / Trap / R&B"
                    className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                    data-testid="radio-form-genre"
                  />
                </div>
              </div>
              <div>
                <label className="label text-white/70 mb-2 block">Track Title</label>
                <input
                  type="text" required maxLength={140}
                  value={form.track_title}
                  onChange={(e) => setForm({ ...form, track_title: e.target.value })}
                  placeholder="Track name"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                  data-testid="radio-form-title"
                />
              </div>
              <div>
                <label className="label text-white/70 mb-2 block">Track Link</label>
                <input
                  type="url" required
                  value={form.track_link}
                  onChange={(e) => setForm({ ...form, track_link: e.target.value })}
                  placeholder="https://open.spotify.com/track/… (Spotify / SoundCloud / YouTube)"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                  data-testid="radio-form-link"
                />
              </div>
              <div>
                <label className="label text-white/70 mb-2 block">Note for the Editors (optional)</label>
                <textarea
                  maxLength={600} rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Tell us anything we should know about the record (release date, collaborators, energy)…"
                  className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none resize-none"
                  data-testid="radio-form-note"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="radio-form-confirm"
              >
                <Upload size={16} /> {submitting ? "Submitting…" : "Send to Editors"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
